import React, { CSSProperties, FC } from 'react'
import { cn } from 'ethcss'
import { ListElement, TableElement } from '../templateEditor.types'
import { getTableColWidths, getTableRowHeights } from '../elements/tableListOps'
import { toDisplaySize } from '../templateEditor.utils'
import styles from './templateEditor__elementChrome.jcss'

type TableChromeProps = {
    element: TableElement
    scale: number
    pointerScale: number
    onInsertRow: (index: number) => void
    onInsertCol: (index: number) => void
    onResizeRow: (borderIndex: number, deltaLogical: number) => void
    onResizeCol: (borderIndex: number, deltaLogical: number) => void
    onResizeGestureStart?: () => void
}

type ListChromeProps = {
    element: ListElement
    scale: number
    itemStep: number
    onInsertItem: (index: number) => void
}

const PLUS_SIZE = 18
const PLUS_OUTSIDE = -22
const PLUS_HALF = PLUS_SIZE / 2

const plusPosition = (along: 'row' | 'col', offset: number): CSSProperties =>
    along === 'row'
        ? { left: PLUS_OUTSIDE, top: Math.max(0, offset - PLUS_HALF) }
        : { top: PLUS_OUTSIDE, left: Math.max(0, offset - PLUS_HALF) }

const InsertPlusButton: FC<{ style: CSSProperties; onInsert: () => void }> = ({ style, onInsert }) => (
    <button
        type="button"
        className={styles.plus}
        style={style}
        title="+"
        onMouseDown={(event) => event.stopPropagation()}
        onClick={(event) => {
            event.stopPropagation()
            onInsert()
        }}
    >
        +
    </button>
)

const startAxisDrag = (
    event: React.MouseEvent,
    axis: 'x' | 'y',
    scaleFactor: number,
    onDelta: (deltaLogical: number) => void,
    onStart?: () => void
) => {
    event.preventDefault()
    event.stopPropagation()
    onStart?.()

    let last = axis === 'x' ? event.clientX : event.clientY
    const factor = scaleFactor > 0 ? scaleFactor : 1

    const onMove = (moveEvent: MouseEvent) => {
        const current = axis === 'x' ? moveEvent.clientX : moveEvent.clientY
        const deltaLogical = (current - last) / factor
        last = current
        if (deltaLogical !== 0) {
            onDelta(deltaLogical)
        }
    }

    const onUp = () => {
        window.removeEventListener('mousemove', onMove)
        window.removeEventListener('mouseup', onUp)
    }

    window.addEventListener('mousemove', onMove)
    window.addEventListener('mouseup', onUp)
}

const cumulativeOffsets = (sizes: number[], scale: number) => {
    const offsets = [0]
    let acc = 0
    for (const size of sizes) {
        acc += size
        offsets.push(toDisplaySize(acc, scale))
    }
    return offsets
}

export const TemplateEditorTableChrome: FC<TableChromeProps> = ({
    element,
    scale,
    pointerScale,
    onInsertRow,
    onInsertCol,
    onResizeRow,
    onResizeCol,
    onResizeGestureStart,
}) => {
    const rowOffsets = cumulativeOffsets(getTableRowHeights(element), scale)
    const colOffsets = cumulativeOffsets(getTableColWidths(element), scale)

    return (
        <div className={styles.chrome}>
            {rowOffsets.map((top, index) => (
                <InsertPlusButton
                    key={`row-plus-${index}`}
                    style={plusPosition('row', top)}
                    onInsert={() => onInsertRow(index)}
                />
            ))}
            {colOffsets.map((left, index) => (
                <InsertPlusButton
                    key={`col-plus-${index}`}
                    style={plusPosition('col', left)}
                    onInsert={() => onInsertCol(index)}
                />
            ))}
            {rowOffsets.map((top, index) => (
                <div
                    key={`row-border-${index}`}
                    className={cn(styles.borderHandle, styles.borderHandleRow)}
                    data-table-resize="row"
                    data-index={index}
                    style={{ top }}
                    onMouseDown={(event) =>
                        startAxisDrag(
                            event,
                            'y',
                            pointerScale,
                            (delta) => onResizeRow(index, delta),
                            onResizeGestureStart
                        )
                    }
                />
            ))}
            {colOffsets.map((left, index) => (
                <div
                    key={`col-border-${index}`}
                    className={cn(styles.borderHandle, styles.borderHandleCol)}
                    data-table-resize="col"
                    data-index={index}
                    style={{ left }}
                    onMouseDown={(event) =>
                        startAxisDrag(
                            event,
                            'x',
                            pointerScale,
                            (delta) => onResizeCol(index, delta),
                            onResizeGestureStart
                        )
                    }
                />
            ))}
        </div>
    )
}

export const TemplateEditorListChrome: FC<ListChromeProps> = ({ element, itemStep, onInsertItem }) => {
    const along = element.orientation === 'horizontal' ? 'col' : 'row'
    const slots = element.items.length + 1

    return (
        <div className={styles.chrome}>
            {Array.from({ length: slots }, (_, index) => (
                <InsertPlusButton
                    key={`item-plus-${index}`}
                    style={plusPosition(along, index * itemStep)}
                    onInsert={() => onInsertItem(index)}
                />
            ))}
        </div>
    )
}
